import { useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import { api, ApiError, MANAGERS, type OrgSummary } from '../api.ts';
import { Webhooks } from './Webhooks.tsx';

/**
 * The home's own settings — RX-ORG-09, RX-ORG-11, RX-TRUST-04.
 *
 * Three things the API has always had and no screen ever reached, each on
 * the reachability list as "no settings screen":
 *
 *  - **When caregivers hear.** A ward wants minutes; a home-care agency with
 *    nurses spread across a district wants longer, because an alert nobody
 *    can act on trains people to ignore alerts. Every home ran on the
 *    defaults because nobody could change them.
 *  - **How the home looks** in the apps its residents' families use.
 *  - **Erasing the organisation.** A home that stops using DrBell is owed
 *    the deletion of every resident's record, and DPDP requires it on
 *    request — and there was no button for it anywhere. Owner only, typed
 *    confirmation, and said plainly: it is every resident at once.
 */
interface PolicyResponse {
  policy: {
    lateAfterMinutes: number;
    criticalLateAfterMinutes: number;
    staffCanSeeHistory: boolean;
    allowFamilyInvites: boolean;
  };
  defaults: PolicyResponse['policy'];
  yourRole: string;
}

interface BrandingResponse {
  branding: { primaryColor: string | null; logoKey: string | null; tagline: string | null };
  logoUrl: string | null;
  organisationName: string;
}

const reason = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));

export function Settings({ org, onErased }: { org: OrgSummary; onErased: () => void }) {
  const canEdit = MANAGERS.includes(org.role);
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [draft, setDraft] = useState<PolicyResponse['policy'] | null>(null);
  const [branding, setBranding] = useState<BrandingResponse | null>(null);
  const [colour, setColour] = useState('');
  const [tagline, setTagline] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Destructive | null>(null);

  const load = async () => {
    try {
      const [p, b] = await Promise.all([
        api.get<PolicyResponse>(`/api/orgs/${org.id}/policy`),
        api.get<BrandingResponse>(`/api/orgs/${org.id}/branding`),
      ]);
      setPolicy(p);
      setDraft(p.policy);
      setBranding(b);
      setColour(b.branding.primaryColor ?? '');
      setTagline(b.branding.tagline ?? '');
    } catch (e) {
      setError(reason(e));
    }
  };

  useEffect(() => { void load(); }, [org.id]);

  if (error) return <p className="rounded-xl border border-dashed border-(--line) px-4 py-8 text-center text-(--muted)">{error}</p>;
  if (!policy || !draft || !branding) return <p className="text-(--muted)">Loading…</p>;

  const policyChanged = JSON.stringify(draft) !== JSON.stringify(policy.policy);
  // The API refuses this too; saying so before Save beats a 400 after it.
  const criticalLooser = draft.criticalLateAfterMinutes > draft.lateAfterMinutes;

  const savePolicy = async () => {
    setBusy('policy');
    try {
      const r = await api.put<{ rearmed?: number }>(`/api/orgs/${org.id}/policy`, draft);
      toast.success(
        'Escalation timings saved',
        r.rearmed ? `Applying to ${r.rearmed} residents now.` : 'They apply to the next dose that runs late.',
      );
      await load();
    } catch (e) {
      toast.error('Not saved', reason(e));
    } finally {
      setBusy(null);
    }
  };

  const saveBranding = async () => {
    setBusy('branding');
    try {
      await api.put(`/api/orgs/${org.id}/branding`, {
        primaryColor: colour.trim() || null,
        tagline: tagline.trim() || null,
      });
      toast.success('Branding saved');
      await load();
    } catch (e) {
      // The colour check explains itself — contrast ratio and what it needs
      // to be — so its message goes through verbatim.
      toast.error('Not saved', reason(e));
    } finally {
      setBusy(null);
    }
  };

  const erase = () =>
    setConfirming({
      title: `Erase ${org.name}?`,
      body: `Every resident's record in ${org.name} — medicines, doses, history, photographs, caregivers and staff access — is deleted permanently. Families lose access to their relatives' records here. This cannot be undone.`,
      reassurance:
        'Families who look after somebody through their own DrBell account keep that account; only what this organisation holds is erased.',
      confirmLabel: 'Erase the organisation',
      typeToConfirm: org.name,
      onConfirm: async () => {
        const r = await api.del<{ queued?: boolean; message?: string; patients?: number }>(`/api/orgs/${org.id}`);
        setConfirming(null);
        if (r.queued) {
          toast.success('Erasure started', r.message);
        } else {
          toast.success(`${org.name} has been erased`, `${r.patients ?? 0} resident records deleted.`);
        }
        onErased();
      },
    });

  const minutes = (key: 'lateAfterMinutes' | 'criticalLateAfterMinutes', label: string, hint: string) => (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      <span className="mt-0.5 block text-xs text-(--muted)">{hint}</span>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={720}
          disabled={!canEdit}
          value={draft[key]}
          onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })}
          className="min-h-11 w-28 rounded-lg border border-(--line) bg-(--panel) px-3"
        />
        <span className="text-sm text-(--muted)">minutes after the dose is due</span>
      </div>
    </label>
  );

  const toggle = (key: 'staffCanSeeHistory' | 'allowFamilyInvites', label: string, hint: string) => (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        disabled={!canEdit}
        checked={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
        className="mt-1 h-5 w-5"
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-(--muted)">{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="max-w-2xl space-y-10">
      {confirming && <Confirm {...confirming} onCancel={() => setConfirming(null)} />}

      {!canEdit && (
        <p className="rounded-lg border border-(--line) bg-(--panel) px-3 py-2 text-sm text-(--muted)">
          Only an owner or admin can change these. You can see what the home is set to.
        </p>
      )}

      <section className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold">When caregivers are told</h2>
          <p className="text-sm text-(--muted)">
            How long a dose may run late before the people looking after a resident hear
            about it. Shorter suits a ward; longer suits a team that has to travel to act.
          </p>
        </div>
        {minutes('lateAfterMinutes', 'Any medicine', `The default is ${policy.defaults.lateAfterMinutes}.`)}
        {minutes(
          'criticalLateAfterMinutes',
          'A medicine marked critical',
          `The default is ${policy.defaults.criticalLateAfterMinutes}. Never later than any medicine — marking one critical is what makes its window tighter.`,
        )}
        {criticalLooser && (
          <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600">
            A critical medicine would be escalated later than an ordinary one. Make it the same or shorter.
          </p>
        )}
        {toggle(
          'staffCanSeeHistory',
          'Staff can see past doses',
          'Off, staff see what is due on their round. On, they can also read each resident’s history.',
        )}
        {toggle(
          'allowFamilyInvites',
          'Families can be invited',
          'Lets the home invite a resident’s relatives to follow their doses from their own phones.',
        )}
        {canEdit && (
          <button
            disabled={!policyChanged || criticalLooser || busy !== null}
            onClick={() => void savePolicy()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy === 'policy' ? 'Saving…' : 'Save timings'}
          </button>
        )}
      </section>

      <section className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold">How the home appears</h2>
          <p className="text-sm text-(--muted)">
            Shown to families and staff alongside DrBell&rsquo;s own name. A colour has to be
            readable as text on white — the check is the same one the apps use.
          </p>
        </div>
        <label className="block">
          <span className="text-sm font-medium">Colour</span>
          <div className="mt-2 flex items-center gap-3">
            <input
              type="color"
              disabled={!canEdit}
              value={/^#[0-9a-f]{6}$/i.test(colour) ? colour : '#025158'}
              onChange={(e) => setColour(e.target.value)}
              className="h-11 w-14 rounded-lg border border-(--line)"
              aria-label="Pick a colour"
            />
            <input
              disabled={!canEdit}
              value={colour}
              onChange={(e) => setColour(e.target.value)}
              // A <label> names only its FIRST field, the picker above, so
              // this one said nothing to a screen reader without its own.
              aria-label="Colour as a hex code, for example #025158"
              placeholder="#025158 — or leave empty for DrBell's own"
              className="min-h-11 flex-1 rounded-lg border border-(--line) bg-(--panel) px-3 font-mono text-sm"
            />
          </div>
        </label>
        <Logo org={org} url={branding.logoUrl} canEdit={canEdit} onChanged={() => void load()} />
        <label className="block">
          <span className="text-sm font-medium">Tagline</span>
          <input
            disabled={!canEdit}
            value={tagline}
            maxLength={60}
            onChange={(e) => setTagline(e.target.value)}
            placeholder={`Care at ${branding.organisationName}`}
            className="mt-2 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
          />
        </label>
        {canEdit && (
          <button
            disabled={
              busy !== null ||
              ((colour.trim() || null) === branding.branding.primaryColor &&
                (tagline.trim() || null) === branding.branding.tagline)
            }
            onClick={() => void saveBranding()}
            className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
          >
            {busy === 'branding' ? 'Saving…' : 'Save branding'}
          </button>
        )}
      </section>

      {/* Managers only, like the routes: an address that receives residents' data. */}
      {canEdit && <Webhooks org={org} />}

      {org.role === 'owner' && (
        <section className="space-y-3 rounded-xl border border-red-500/30 p-4">
          <h2 className="text-lg font-semibold text-red-600">Erase this organisation</h2>
          <p className="text-sm text-(--muted)">
            Deletes every resident&rsquo;s record held here, permanently. Use it when the home
            stops using DrBell, or when asked to under data-protection law. Export anything
            you are required to keep first.
          </p>
          <button
            onClick={erase}
            className="min-h-11 rounded-lg border border-red-500 px-4 text-sm font-medium text-red-600 hover:bg-red-500/10"
          >
            Erase {org.name}…
          </button>
        </section>
      )}
    </div>
  );
}

/**
 * The home's logo — RX-ORG-11.
 *
 * PNG, JPEG or WebP up to 1MB; the server checks the bytes. Shown in this
 * console's header and, for residents, in the family's app and on the web.
 */
function Logo({ org, url, canEdit, onChanged }: { org: OrgSummary; url: string | null; canEdit: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<Destructive | null>(null);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      toast.error('Not a logo we can use', 'Choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error('Too large', `A logo can be at most 1MB; this one is ${(file.size / 1024 / 1024).toFixed(1)}MB.`);
      return;
    }
    setBusy(true);
    try {
      await api.upload(`/api/orgs/${org.id}/branding/logo`, file);
      toast.success('Logo updated');
      onChanged();
    } catch (e) {
      toast.error('Could not upload the logo', reason(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="block">
      <span className="text-sm font-medium">Logo</span>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-(--line) bg-white">
          {url ? (
            <img src={url} alt={`${org.name} logo`} className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-(--muted)">None</span>
          )}
        </div>
        {canEdit && (
          <>
            <label className={`min-h-11 cursor-pointer rounded-lg border border-(--line) px-4 py-2.5 text-sm hover:border-(--accent) ${busy ? 'opacity-40' : ''}`}>
              {busy ? 'Uploading…' : url ? 'Replace…' : 'Upload…'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={busy}
                className="sr-only"
                onChange={(e) => {
                  void upload(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {url && (
              <button
                disabled={busy}
                onClick={() =>
                  setConfirming({
                    title: 'Remove the logo?',
                    body: 'Families and staff see the home’s name in its place.',
                    confirmLabel: 'Remove logo',
                    onConfirm: async () => {
                      setConfirming(null);
                      try {
                        await api.del(`/api/orgs/${org.id}/branding/logo`);
                        toast.success('Logo removed');
                        onChanged();
                      } catch (e) {
                        toast.error('Could not remove it', reason(e));
                      }
                    },
                  })
                }
                className="min-h-11 rounded-lg px-3 text-sm text-(--muted) hover:text-red-600"
              >
                Remove
              </button>
            )}
          </>
        )}
      </div>
      <p className="mt-1 text-xs text-(--muted)">PNG, JPEG or WebP, up to 1MB. A square logo on a plain background reads best.</p>
      {confirming && <Confirm {...confirming} onCancel={() => setConfirming(null)} />}
    </div>
  );
}

